"use client"
import "../Edash.css"
import {
  Box,
  Text,
  Skeleton,
  Tooltip,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Badge,
  Icon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Flex,
  HStack,
  InputGroup,
  InputLeftElement,
  Input,
  IconButton,
  Spacer,
} from "@chakra-ui/react"
import { useEffect, useState, useRef, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Link, useNavigate } from "react-router-dom"
import {
  fetchJobListAsync,
  selectJobList,
  selectJobListTotal,
} from "../../employers/Employers Slice/employerJobList/employerJobListSlice"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlus, faArrowsRotate, faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons"
import noData from "./nodata.png"
import { TimeDifference } from "../../common/TimeDifference"
import { JobPublishOrUnpublishAsync } from "../employerDashboardSlice/JobPublish/JobPublishSlice"
import { UrgentHiringTagAsync } from "../employerDashboardSlice/UrgentHiringTag/UrgentHiringTagSlice"
import {
  EyeIcon,
  EyeSlashIcon,
  PlusIcon,
  BriefcaseIcon,
  MapPinIcon,
  UserGroupIcon,
  CalendarDaysIcon,
  ClockIcon,
  FireIcon,
} from "@heroicons/react/24/outline"
import { fetchProfileAsync, selectCompanyProfile } from "../employerDashAPIs/companyProfile/CompanyProfileSlice"

// Utility: deadline status
const getDeadlineStatus = (deadlineDate) => {
  if (!deadlineDate) return { isExpired: false, daysLeft: null, isWarning: false }
  const deadline = new Date(deadlineDate)
  const today = new Date()
  deadline.setHours(0, 0, 0, 0)
  today.setHours(0, 0, 0, 0)
  const timeDiff = deadline.getTime() - today.getTime()
  const daysLeft = Math.ceil(timeDiff / (1000 * 3600 * 24))
  return {
    isExpired: daysLeft < 0,
    daysLeft: daysLeft >= 0 ? daysLeft : Math.abs(daysLeft),
    isWarning: daysLeft >= 0 && daysLeft <= 3,
  }
}

// Status -> color mapping
const getStatusColor = (status) => {
  switch ((status || "").toLowerCase()) {
    case "active":
      return { colorScheme: "green", bg: "bg-green-100", text: "text-green-800", border: "border-green-200" }
    case "inactive":
      return { colorScheme: "gray", bg: "bg-gray-100", text: "text-gray-800", border: "border-gray-200" }
    case "completed":
      return { colorScheme: "blue", bg: "bg-blue-100", text: "text-blue-800", border: "border-blue-200" }
    case "cancelled":
      return { colorScheme: "orange", bg: "bg-orange-100", text: "text-orange-800", border: "border-orange-200" }
    default:
      return { colorScheme: "gray", bg: "bg-gray-100", text: "text-gray-800", border: "border-gray-200" }
  }
}

// Tabs config
const JOB_TABS = [
  { key: "all", label: "All Jobs", params: {} },
  { key: "active", label: "Active", params: { status: "active" } },
  { key: "inactive", label: "Inactive", params: { status: "inactive" } },
  { key: "completed", label: "Completed", params: { status: "completed" } },
  { key: "cancelled", label: "Paused", params: { status: "cancelled" } },
  { key: "unpublished", label: "Unpublished", params: { is_published: 0 } },
  { key: "published", label: "Published", params: { is_published: 1 } },
]

// Action dialog labels
const actionLabels = {
  published: {
    title: "Publish Job",
    body: "Are you sure you want to publish this job?",
    confirmColor: "green",
  },
  unpublished: {
    title: "Unpublish Job",
    body: "Are you sure you want to unpublish this job?",
    confirmColor: "red",
  },
}

export function ManageJobs() {
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [selectedJob, setSelectedJob] = useState(null)
  const [actionType, setActionType] = useState("")
  const cancelRef = useRef()
  const [isUrgentDialogOpen, setIsUrgentDialogOpen] = useState(false)
  const [urgentActionType, setUrgentActionType] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [searchTerm, setSearchTerm] = useState("")
  const [isRefreshing, setIsRefreshing] = useState(false)

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const jobs = useSelector(selectJobList)
  const totalJobs = useSelector(selectJobListTotal)
  const companyDataActive = useSelector(selectCompanyProfile)
  const toast = useToast()

  // Pagination
  const jobsPerPage = 15
  const [currentPages, setCurrentPages] = useState({
    all: 1,
    active: 1,
    inactive: 1,
    completed: 1,
    cancelled: 1,
    unpublished: 1,
    published: 1,
  })

  // Derived helpers
  const currentTabKey = JOB_TABS[activeTab].key
  const currentPage = currentPages[currentTabKey]
  const totalPages = Math.max(1, Math.ceil((totalJobs ?? 0) / jobsPerPage))

  // Subscription-based Post Job enable/disable
  const activeSub = companyDataActive?.active_subscription
  const isDisabledPostJob = !activeSub || activeSub.remaining_job_post === 0
  const tooltipLabel = !activeSub
    ? "You don't have any active subscription."
    : activeSub.remaining_job_post === 0
      ? "You have no remaining job posts in your subscription."
      : ""

  // Fetch company profile once
  useEffect(() => {
    dispatch(fetchProfileAsync())
  }, [dispatch])

  // Reset page when tab changes
  useEffect(() => {
    const tabKey = JOB_TABS[activeTab].key
    setCurrentPages((prev) => ({ ...prev, [tabKey]: 1 }))
    setSearchTerm("")
  }, [activeTab])

  // Fetch jobs
  const performFetch = async () => {
    setIsLoading(true)
    setHasError(false)
    const currentTabParams = JOB_TABS[activeTab].params
    const currentTabPage = currentPages[JOB_TABS[activeTab].key]
    try {
      await dispatch(
        fetchJobListAsync({
          page: currentTabPage,
          ...currentTabParams,
        })
      ).unwrap()
    } catch (e) {
      setHasError(true)
      toast({
        title: "Failed to load job listings.",
        description: "Please try again later.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      })
    } finally {
      setIsLoading(false)
      setIsRefreshing(false)
    }
    window.scrollTo(0, 0)
  }

  useEffect(() => {
    performFetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, activeTab, currentPages])

  // Actions: Publish/Unpublish
  const handlePublishAction = (job, action) => {
    setSelectedJob(job)
    setActionType(action) // "published" or "unpublished"
    setIsDialogOpen(true)
  }

  const handleConfirmAction = async () => {
    if (!selectedJob || !actionType) return
    try {
      const result = await dispatch(
        JobPublishOrUnpublishAsync({
          uuid: selectedJob.uuid,
          keyword: actionType,
        })
      ).unwrap()

      if (result.status !== "success") {
        throw new Error(result.message || "Failed to update job status.")
      }

      toast({
        title: `Job ${actionType} successfully.`,
        status: "success",
        duration: 2500,
        isClosable: true,
        position: "top-right",
      })

      await performFetch()
    } catch (error) {
      toast({
        title: "Action failed",
        description: "Check subscription and try again or contact support.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      })
    } finally {
      setIsDialogOpen(false)
    }
  }

  // Actions: Urgent hiring
  const [pendingJobRequests, setPendingJobRequests] = useState({})
  const handleUrgentHiringAction = (job, isUrgent) => {
    setSelectedJob(job)
    setUrgentActionType(isUrgent)
    setIsUrgentDialogOpen(true)
  }

  const handleConfirmUrgentAction = async () => {
    if (!selectedJob) return
    try {
      setPendingJobRequests((prev) => ({ ...prev, [selectedJob.uuid]: true }))

      const result = await dispatch(
        UrgentHiringTagAsync({
          uuid: selectedJob.uuid,
          urgent_hiring_tag: urgentActionType,
        })
      ).unwrap()

      if (result.status !== "success") {
        throw new Error(result.message || "Failed to update urgent hiring status.")
      }

      toast({
        title: `Urgent hiring tag ${urgentActionType ? "enabled" : "disabled"} successfully.`,
        status: "success",
        duration: 2500,
        isClosable: true,
        position: "top-right",
      })

      await performFetch()
    } catch (error) {
      toast({
        title: "Action failed",
        description: "Check subscription and try again or contact support.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      })
    } finally {
      setPendingJobRequests((prev) => {
        const next = { ...prev }
        delete next[selectedJob.uuid]
        return next
      })
      setIsUrgentDialogOpen(false)
    }
  }

  const handleApplicantClick = (job) => {
    navigate(`/employer-dashboard/applicants/${job.uuid}`)
  }

  // Pagination
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPages((prev) => ({
        ...prev,
        [currentTabKey]: prev[currentTabKey] + 1,
      }))
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
  }

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPages((prev) => ({
        ...prev,
        [currentTabKey]: prev[currentTabKey] - 1,
      }))
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
  }

  // In-page search (filters current page results only)
  const filteredJobs = useMemo(() => {
    if (!Array.isArray(jobs)) return []
    const q = (searchTerm || "").trim().toLowerCase()
    if (!q) return jobs
    return jobs.filter((job) => {
      const title = job?.job_title?.title || ""
      const name = job?.job_name || ""
      const company = job?.hiring_company_name || ""
      const locs = (job?.job_locations || []).map((l) => l?.job_location?.title || "").join(" ")
      return [title, name, company, locs].some((t) => (t || "").toLowerCase().includes(q))
    })
  }, [jobs, searchTerm])

  const renderJobCards = () => {
    if (isLoading) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-3 w-full">
          {[...Array(6)].map((_, index) => (
            <div key={index} className="bg-white rounded-xl shadow-md border border-gray-100 p-3">
              <div className="space-y-4">
                <div className="flex justify-between items-start">
                  <Skeleton height="20px" width="70%" />
                  <Skeleton height="24px" width="60px" />
                </div>
                <Skeleton height="16px" width="60%" />
                <Skeleton height="16px" width="100%" />
                <Skeleton height="16px" width="80%" />
                <div className="space-y-2">
                  <Skeleton height="36px" width="100%" />
                  <Skeleton height="36px" width="100%" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )
    }

    if (hasError) {
      return (
        <Box textAlign="center" py="10">
          <Text fontSize="xl" color="red.500">Failed to load job listings. Please try again later.</Text>
          <Button mt={4} onClick={() => performFetch()} leftIcon={<FontAwesomeIcon icon={faArrowsRotate} />}>
            Retry
          </Button>
        </Box>
      )
    }

    if (!filteredJobs || filteredJobs.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center w-full py-10">
          <img src={noData || "/placeholder.svg"} className="object-contain h-[220px] w-[220px]" alt="No jobs" />
          <h3 className="text-lg font-semibold text-gray-800 mb-2">No {JOB_TABS[activeTab].label} Found</h3>
          <p className="text-gray-600 text-center max-w-md mb-4">
            {searchTerm
              ? "No results matched your search on this page."
              : `We couldn't find any ${JOB_TABS[activeTab].label.toLowerCase()} jobs at the moment.`}
          </p>
          <HStack spacing={3}>
            {activeTab === 0 && (
              <Link to={`/employer-dashboard/post-job`}>
                <Button colorScheme="blue" leftIcon={<PlusIcon className="h-4 w-4" />}>
                  Post Your Job
                </Button>
              </Link>
            )}
            {searchTerm && (
              <Button variant="outline" onClick={() => setSearchTerm("")}>
                Clear Search
              </Button>
            )}
          </HStack>
        </div>
      )
    }

    return (
      <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-3 w-full">
          {filteredJobs.map((job) => {
            const statusColors = getStatusColor(job.status)
            const deadlineInfo = getDeadlineStatus(job.job_deadline)

            return (
              <div
                key={job.uuid}
                className="bg-white rounded-xl border border-gray-100 transition-all duration-200 overflow-hidden relative hover:shadow-md"
              >
                {/* Status badge */}
                <div className="absolute top-3 right-3 z-10">
                  <div
                    className={`px-2 py-1 rounded-full text-xs font-semibold ${statusColors.bg} ${statusColors.text} ${statusColors.border} border`}
                  >
                    {job.status?.charAt(0).toUpperCase() + job.status?.slice(1) || "Unknown"}
                  </div>
                </div>

                {/* Header */}
                <div className="p-3 pb-2">
                  <div className="flex items-start gap-3 pr-16">
                    <div className="bg-blue-50 p-3 rounded-lg flex-shrink-0">
                      <BriefcaseIcon className="h-6 w-6 text-blue-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <Link to={`/employer-dashboard/manage-jobs-details/${job.uuid}`}>
                        <h3 className="text-lg font-bold text-gray-800 hover:text-blue-600 hover:underline line-clamp-2 mb-1 leading-tight">
                          {job.job_title?.title ? job.job_title?.title : "No Job Title"}
                        </h3>
                      </Link>
                      <Tooltip label="Job Name" placement="top" bg="gray.100" color="black" fontSize="sm">
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <BriefcaseIcon className="h-4 w-4" />
                          <span className="truncate">{job.job_name || "—"}</span>
                        </div>
                      </Tooltip>
                    </div>
                  </div>
                </div>

                {/* Body */}
                <div className="px-3 pb-3">
                  <div className="space-y-3">
                    {/* Company */}
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <BriefcaseIcon className="h-4 w-4 flex-shrink-0" />
                      <Tooltip label="Company Name" placement="top" bg="gray.100" color="black" fontSize="sm">
                        <span className="truncate font-medium">{job.hiring_company_name || "—"}</span>
                      </Tooltip>
                    </div>

                    {/* Locations */}
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <MapPinIcon className="h-4 w-4 flex-shrink-0" />
                      <Tooltip label="Location(s)" placement="top" bg="gray.100" color="black" fontSize="sm">
                        <span className="truncate">
                          {job.job_locations
                            ?.map((loc) => loc.job_location?.title)
                            .filter(Boolean)
                            .join(", ") || "Not specified"}
                        </span>
                      </Tooltip>
                    </div>

                    {/* No of hire */}
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <UserGroupIcon className="h-4 w-4 flex-shrink-0" />
                      <Tooltip label="Number of Openings" placement="top" bg="gray.100" color="black" fontSize="sm">
                        <span className="truncate font-medium">No. of Hire: {job?.no_of_hire ?? "—"}</span>
                      </Tooltip>
                    </div>

                    {/* Apps + Created meta */}
                    <div className="flex items-center flex-wrap gap-2 justify-between py-2 bg-gray-50 rounded-lg px-3">
                      <div className="flex items-center gap-2">
                        <UserGroupIcon className="h-4 w-4 text-blue-500" />
                        <span className="text-sm text-gray-600">Applications:</span>
                        <Button
                          variant="link"
                          colorScheme="blue"
                          size="sm"
                          onClick={() => handleApplicantClick(job)}
                        >
                          {job.applicants_count} Applicants
                        </Button>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <CalendarDaysIcon className="h-4 w-4" />
                        <span>Created:</span>
                        <span className="font-medium">
                          <TimeDifference dateTimeString={job.created_at} />
                        </span>
                      </div>
                    </div>

                    {/* Status row */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        {/* Published */}
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 font-medium">PUBLISHED:</span>
                          <Badge colorScheme={job.is_published ? "green" : "red"} size="sm" px={2} py={1} borderRadius="md">
                            {job.is_published ? "Published" : "Unpublished"}
                          </Badge>
                        </div>

                        {/* Urgent */}
                        <div className="flex items-center gap-2">
                          <FireIcon
                            className={`h-4 w-4 ${job.urgent_hiring_tag_enable === 1 ? "text-red-500" : "text-gray-400"}`}
                          />
                          <span className="text-xs text-gray-500 font-medium">URGENT:</span>
                          <Badge
                            colorScheme={job.urgent_hiring_tag_enable === 1 ? "red" : "gray"}
                            size="sm"
                            px={2}
                            py={1}
                            borderRadius="md"
                          >
                            {job.urgent_hiring_tag_enable === 1 ? "Yes" : "No"}
                          </Badge>
                        </div>
                      </div>

                      {/* Deadline */}
                      {job.job_deadline && (
                        <div className="flex items-center gap-2">
                          <ClockIcon className="h-4 w-4 text-gray-500" />
                          <span className="text-xs text-gray-500 font-medium">DEADLINE:</span>
                          {(() => {
                            if (deadlineInfo.isExpired) {
                              return (
                                <Tooltip label={`Deadline was ${deadlineInfo.daysLeft} day(s) ago`} hasArrow>
                                  <Badge colorScheme="red" size="sm" px={2} py={1} borderRadius="md">
                                    Expired {deadlineInfo.daysLeft}d ago
                                  </Badge>
                                </Tooltip>
                              )
                            } else if (deadlineInfo.isWarning) {
                              return (
                                <Tooltip label="Closing soon" hasArrow>
                                  <Badge colorScheme="orange" size="sm" px={2} py={1} borderRadius="md">
                                    {deadlineInfo.daysLeft === 0 ? "Today" : `${deadlineInfo.daysLeft}d left`}
                                  </Badge>
                                </Tooltip>
                              )
                            } else if (deadlineInfo.daysLeft !== null) {
                              return (
                                <Badge colorScheme="blue" size="sm" px={2} py={1} borderRadius="md">
                                  {deadlineInfo.daysLeft}d left
                                </Badge>
                              )
                            }
                            return <span className="text-sm text-gray-500">Not set</span>
                          })()}
                        </div>
                      )}
                    </div>

                    <div className="border-t border-gray-100 my-3"></div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2 justify-center w-full">
                      {job.is_published ? (
                        <Button
                          size="sm"
                          colorScheme="red"
                          variant="solid"
                          leftIcon={<Icon as={EyeSlashIcon} boxSize={4} />}
                          onClick={() => handlePublishAction(job, "unpublished")}
                          _hover={{ transform: "translateY(-1px)", boxShadow: "md" }}
                          transition="all 0.2s"
                          flex="1"
                          minW="200px"
                        >
                          Unpublish Job
                        </Button>
                      ) : (
                        <Tooltip
                          label={
                            getDeadlineStatus(job.job_deadline).isExpired
                              ? "Cannot publish job because the deadline has expired"
                              : "Publish this job to make it visible"
                          }
                          placement="top"
                          hasArrow
                          bg="gray.700"
                          color="white"
                        >
                          <Button
                            size="sm"
                            colorScheme="green"
                            variant="solid"
                            leftIcon={<Icon as={EyeIcon} boxSize={4} />}
                            onClick={() => handlePublishAction(job, "published")}
                            isDisabled={getDeadlineStatus(job.job_deadline).isExpired}
                            _hover={{ transform: "translateY(-1px)", boxShadow: "md" }}
                            _disabled={{
                              opacity: 0.6,
                              cursor: "not-allowed",
                              transform: "none",
                            }}
                            transition="all 0.2s"
                            flex="1"
                            minW="200px"
                          >
                            Publish Job
                          </Button>
                        </Tooltip>
                      )}

                      {job.urgent_hiring_tag_enable === 1 ? (
                        <Button
                          colorScheme="red"
                          size="sm"
                          isLoading={pendingJobRequests[job.uuid]}
                          onClick={() => handleUrgentHiringAction(job, false)}
                          variant="outline"
                          leftIcon={<Icon as={FireIcon} boxSize={4} />}
                          flex="1"
                          minW="200px"
                        >
                          Disable Urgent Hiring
                        </Button>
                      ) : (
                        <Tooltip
                          label={
                            getDeadlineStatus(job.job_deadline).isExpired
                              ? "Cannot enable urgent tag because the deadline has expired"
                              : "Highlight this job as urgent"
                          }
                          hasArrow
                        >
                          <Button
                            colorScheme="orange"
                            size="sm"
                            isLoading={pendingJobRequests[job.uuid]}
                            onClick={() => handleUrgentHiringAction(job, true)}
                            variant="outline"
                            isDisabled={getDeadlineStatus(job.job_deadline).isExpired}
                            leftIcon={<Icon as={FireIcon} boxSize={4} />}
                            flex="1"
                            minW="200px"
                          >
                            Enable Urgent Hiring
                          </Button>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Pagination */}
        <Box mt={8} w="full">
          <Flex justify="center" align="center">
            <HStack spacing={4} align="center">
              <Button colorScheme="gray" variant="solid" onClick={handlePrevPage} isDisabled={currentPage === 1} size="md">
                <span className="hidden sm:inline">Previous</span>
                <span className="sm:hidden">Prev</span>
              </Button>

              <Text fontSize="md" fontWeight="medium" color="gray.700" px={4}>
                <span className="hidden sm:inline">
                  Page {currentPage} of {totalPages}
                </span>
                <span className="sm:hidden">
                  {currentPage}/{totalPages}
                </span>
              </Text>

              <Button
                colorScheme="blue"
                variant="solid"
                onClick={handleNextPage}
                isDisabled={currentPage === totalPages}
                size="md"
              >
                Next
              </Button>
            </HStack>
          </Flex>
        </Box>
      </>
    )
  }

  return (
    <div className="">
      <div className="bg-mainBg p-3">
        <div className="px-2 sm:px-4 sm:py-4 min-h-screen">
          {/* Header row */}
          <Flex justify="space-between" align="center" flexWrap="wrap" gap={2} mb={3}>
            <Box>
              <h1 className="sm:text-4xl text-2xl font-semibold text-left">Manage Jobs</h1>
              <Text fontSize="sm" color="gray.600">Create, publish, and manage your job postings</Text>
            </Box>

            <HStack>
              <InputGroup size="sm" w={{ base: "full", md: "280px" }}>
                <InputLeftElement pointerEvents="none">
                  <FontAwesomeIcon icon={faMagnifyingGlass} className="text-gray-400" />
                </InputLeftElement>
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search current page..."
                  bg="white"
                />
              </InputGroup>

              <Tooltip label="Refresh" hasArrow>
                <IconButton
                  aria-label="Refresh"
                  icon={<FontAwesomeIcon icon={faArrowsRotate} />}
                  onClick={() => {
                    setIsRefreshing(true)
                    performFetch()
                  }}
                  isLoading={isRefreshing}
                  variant="outline"
                />
              </Tooltip>

              <Tooltip label={isDisabledPostJob ? tooltipLabel : ""} isDisabled={!isDisabledPostJob} hasArrow bg="red.500" color="white">
                <span>
                  <Link to="/employer-dashboard/post-job">
                    <Button colorScheme="blue" isDisabled={isDisabledPostJob} cursor={isDisabledPostJob ? "not-allowed" : "pointer"} opacity={isDisabledPostJob ? 0.6 : 1}>
                      <FontAwesomeIcon icon={faPlus} className="mr-2" />
                      Post Job
                    </Button>
                  </Link>
                </span>
              </Tooltip>
            </HStack>
          </Flex>

          {/* Tabs + label */}
          <div className="w-full bg-white rounded-xl">
            <div className="px-7 py-7">
              <Flex align="center" >
                <Text className="text-lg font-semibold">My Job Listings</Text>
                <Spacer />
                {/* Show total count of current filter */}
                <Badge colorScheme="blue" variant="subtle" px={3} py={1} borderRadius="full">
                  Total: {totalJobs ?? 0}
                </Badge>
              </Flex>

              <Tabs index={activeTab} onChange={(index) => setActiveTab(index)} colorScheme="blue" isFitted>
                <TabList className="flex flex-wrap">
                  {JOB_TABS.map((tab, idx) => (
                    <Tab key={tab.key}>
                      {tab.label}
                      {idx === activeTab && (
                        <Badge ml={2} colorScheme="blue" variant="subtle">
                          {totalJobs ?? 0}
                        </Badge>
                      )}
                    </Tab>
                  ))}
                </TabList>
                <TabPanels>
                  {JOB_TABS.map((tab) => (
                    <TabPanel key={tab.key} px={0}>
                      {renderJobCards()}
                    </TabPanel>
                  ))}
                </TabPanels>
              </Tabs>
            </div>
          </div>
        </div>
      </div>

      {/* Publish/Unpublish Confirmation */}
      <AlertDialog isOpen={isDialogOpen} leastDestructiveRef={cancelRef} onClose={() => setIsDialogOpen(false)} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {actionLabels[actionType]?.title || "Confirm Action"}
            </AlertDialogHeader>
            <AlertDialogBody>{actionLabels[actionType]?.body || "Are you sure you want to proceed?"}</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setIsDialogOpen(false)} variant="outline" colorScheme="gray">
                No
              </Button>
              <Button colorScheme={actionLabels[actionType]?.confirmColor || "blue"} onClick={handleConfirmAction} ml={3}>
                Yes
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Urgent Hiring Confirmation */}
      <AlertDialog isOpen={isUrgentDialogOpen} leastDestructiveRef={cancelRef} onClose={() => setIsUrgentDialogOpen(false)} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {urgentActionType ? "Enable Urgent Hiring" : "Disable Urgent Hiring"}
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to {urgentActionType ? "enable" : "disable"} urgent hiring tag for this job?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setIsUrgentDialogOpen(false)} variant="outline" colorScheme="gray">
                No
              </Button>
              <Button colorScheme={urgentActionType ? "red" : "gray"} onClick={handleConfirmUrgentAction} ml={3}>
                Yes
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </div>
  )
}